/**
 * Which account a provider collection reads with, and how to stop.
 *
 * Only rendered for a collection that mirrors an external system. The grant
 * itself is shared by every collection on the same system, so disconnecting
 * here revokes it everywhere -- the copy says so rather than implying this one
 * collection can be unhooked on its own.
 */

import { useState } from "react";
import { Plug, Unplug } from "lucide-react";

import {
  useDeleteConnectionMutation,
  useGetConnectionsQuery,
  useSetCollectionProviderMutation,
} from "@/api/api";
import { useCollection } from "@/api/hooks";
import { Button } from "../button";
import { Switch } from "../switch";
import { Field, FieldContent, FieldDescription, FieldLabel } from "../field";
import {
  connectErrorMessage,
  useConnectProvider,
} from "@/providers/external/connect";
import {
  SHOW_IN_ALL,
  getProvider,
  showsInAllCollections,
} from "@/providers/external/registry";
import { forgetToken } from "@/providers/external/tokens";

export default function CollectionConnection() {
  const collection = useCollection();
  const connect = useConnectProvider();
  const [setCollectionProvider] = useSetCollectionProviderMutation();
  const [deleteConnection] = useDeleteConnectionMutation();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const { data: connections } = useGetConnectionsQuery(undefined, {
    skip: !collection?.provider_key,
  });

  const provider = getProvider(collection?.provider_key);
  if (!collection || !provider) return null;

  const Icon = provider.Icon;
  const connection = connections?.find(
    (c) => c.id === collection.connection_id,
  );
  const needsReauth = connection?.status !== "active";
  // The subcollections are facets of the root, so the settings that describe
  // the provider as a whole belong to the root alone.
  const isRoot = !collection.provider_root_id;
  const showInAll = showsInAllCollections(collection.provider_config);

  const toggleShowInAll = (next: boolean) => {
    // provider_config is replaced wholesale server-side, so the rest of it has
    // to be carried across here rather than patched there.
    setCollectionProvider({
      collectionID: collection.id,
      provider_config: {
        ...(collection.provider_config ?? {}),
        [SHOW_IN_ALL]: next,
      },
    });
  };

  const onConnect = async () => {
    setBusy(true);
    setProblem(null);
    const result = await connect(provider.systemId, provider.scopes);
    if (result.status === "ok" && result.connection) {
      await setCollectionProvider({
        collectionID: collection.id,
        connection_id: result.connection,
        set_connection: true,
      });
    }
    setProblem(connectErrorMessage(result));
    setBusy(false);
  };

  const onDisconnect = async () => {
    if (!connection) return;
    setBusy(true);
    setProblem(null);
    // Drop the cached access token first: the row is about to go, and a stale
    // token in memory would keep working until it expired.
    forgetToken(connection.id);
    await deleteConnection(connection.id);
    setBusy(false);
  };

  return (
    <div className="rounded-[10px] bg-secondary border border-border px-4 py-3 flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 shrink-0" />
        <p className="text-sm font-medium">{provider.title}</p>
      </div>

      <p className="text-xs text-muted-foreground">
        {connection && !needsReauth ? (
          <>
            Reading as{" "}
            <span className="text-foreground">
              {connection.account_label ?? "a connected account"}
            </span>
            . Records are fetched in your browser and never stored here.
          </>
        ) : connection ? (
          <>
            Access to {connection.account_label ?? provider.title} has expired.
          </>
        ) : (
          <>
            Not connected yet. Nothing is shown until you grant access, and
            access can be withdrawn at any time.
          </>
        )}
      </p>

      {problem && <p className="text-xs text-destructive">{problem}</p>}

      {isRoot && (
        <Field orientation="horizontal" className="border-t border-border pt-3">
          <FieldContent>
            <FieldLabel
              htmlFor="providerShowInAll"
              className="text-[13px] font-semibold"
            >
              Show on the all-collections timeline
            </FieldLabel>
            <FieldDescription className="text-[12px]">
              Off by default: {provider.title} is dense enough to bury the
              records you wrote yourself. It is always shown inside this
              collection.
            </FieldDescription>
          </FieldContent>
          <Switch
            id="providerShowInAll"
            checked={showInAll}
            onCheckedChange={toggleShowInAll}
          />
        </Field>
      )}

      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={onConnect} disabled={busy}>
          <Plug className="h-4 w-4" />
          {connection ? "Reconnect" : `Connect ${provider.title}`}
        </Button>
        {connection && (
          <Button
            size="sm"
            variant="outline"
            onClick={onDisconnect}
            disabled={busy}
            // Every collection on this system shares the grant, so the label
            // must not read as if it only affected this one.
            title={`Revokes access for every ${provider.systemId} collection`}
          >
            <Unplug className="h-4 w-4" />
            Disconnect
          </Button>
        )}
      </div>
    </div>
  );
}
